import { createSlice, nanoid, type PayloadAction } from '@reduxjs/toolkit';

export interface Toast {
  id: string;
  message: string;
  severity: 'success' | 'info' | 'warning' | 'error';
  /** Optional action label; the handler is registered by the component. */
  actionLabel?: string;
}

interface UiState {
  navCollapsed: boolean;
  commandPaletteOpen: boolean;
  toasts: Toast[];
}

const STORAGE_KEY = 'menuvera:ui';

function loadPreferences(): Pick<UiState, 'navCollapsed'> {
  const fallback = { navCollapsed: false };
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as { navCollapsed?: boolean };
    return { navCollapsed: Boolean(parsed.navCollapsed) };
  } catch {
    return fallback;
  }
}

function savePreferences(state: UiState): void {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ navCollapsed: state.navCollapsed }),
    );
  } catch {
    // Private browsing and full quotas both throw here; a lost preference is
    // not worth breaking a click over.
  }
}

const initialState: UiState = {
  ...loadPreferences(),
  commandPaletteOpen: false,
  toasts: [],
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setNavCollapsed(state, action: PayloadAction<boolean>) {
      state.navCollapsed = action.payload;
      savePreferences(state);
    },
    setCommandPaletteOpen(state, action: PayloadAction<boolean>) {
      state.commandPaletteOpen = action.payload;
    },
    notify: {
      reducer(state, action: PayloadAction<Toast>) {
        state.toasts.push(action.payload);
      },
      prepare(message: string, severity: Toast['severity'] = 'success') {
        return { payload: { id: nanoid(), message, severity } };
      },
    },
    dismissToast(state, action: PayloadAction<string>) {
      state.toasts = state.toasts.filter((toast) => toast.id !== action.payload);
    },
  },
});

export const { setNavCollapsed, setCommandPaletteOpen, notify, dismissToast } = uiSlice.actions;

export const uiReducer = uiSlice.reducer;
